// Wave AW.3-React (2026-05-16) — 외부 바이어 경매 카드
// Wave UX-2.1 Item 1 sweep (2026-05-23) — 4 locale i18n 마이그레이션.
// ERP AuctionCard.js 와 별개 (admin/seller 분기 제거, 순수 buyer view)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthImage from './AuthImage';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import { API } from '../../utils/api';
import { useT } from '../../i18n/I18nProvider';
import { formatKrwAmount } from '../../i18n/currency';
import { showToast } from './Toast';

// 카드는 좁은 공간 — base only (환산 suffix 는 큰 화면 detail/modal 에서)
function formatKrw(n, t) { return formatKrwAmount(n, t); }
function formatMileage(km) {
  if (km == null) return '';
  return Number(km).toLocaleString() + ' km';
}

// 남은 시간 — D-N 또는 H시간 M분 (locale-aware)
function formatRemaining(endsAt, t) {
  if (!endsAt) return '';
  var end = new Date(endsAt).getTime();
  var now = Date.now();
  var diff = end - now;
  if (diff <= 0) return t ? t('cardEnded') : '마감';
  var d = Math.floor(diff / 86400000);
  var h = Math.floor((diff % 86400000) / 3600000);
  var m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return 'D-' + d;
  if (h > 0) return t ? t('cardDurationHm', { h: h, m: m }) : (h + '시간 ' + m + '분');
  return t ? t('cardDurationM', { m: m }) : (m + '분');
}

function AuctionBuyerCard(props) {
  var t = useT();
  var navigate = useNavigate();
  var auction = props.auction || {};
  var onClick = props.onClick;
  var onFavoriteChange = props.onFavoriteChange;

  // Wave AW.3-React Wave 3 fix (2026-05-16) — mask 응답 키 정합.
  // mask.buildPublicAuctionResponse 는 top-level myFavorited (boolean) / myBid (object) 만 노출,
  // myStatus 키 없음. Wave 1 이후 카드 진입 시 즐겨찾기 별/본인 입찰 뱃지 자동 표시 누락 버그 회귀 가드.
  var initialIsFav = !!auction.myFavorited;
  var [isFav, setIsFav] = useState(initialIsFav);
  var [favLoading, setFavLoading] = useState(false);
  // Wave UX-2.3 #16 — 사진 캐러셀 (현재 index)
  var [photoIdx, setPhotoIdx] = useState(0);

  var car = auction.car || {};
  var liveStats = auction.liveStats || {};

  // Wave UX-2.3 #16 — 사진 N 장 (max 5 — 카드 perf 가드)
  var photoCount = Math.min((auction.photos && auction.photos.length) || 0, 5);
  var photoPath = photoCount > 0
    ? ('/buyer-app/auction/' + auction.id + '/photo/' + photoIdx)
    : null;

  var toggleFav = function(e) {
    e.stopPropagation();
    if (favLoading) return;
    setFavLoading(true);
    var prev = isFav;
    setIsFav(!prev);
    authBuyerFetch(API + '/buyer-app/auction/' + auction.id + '/favorite', { method: 'POST' })
      .then(function(r) {
        // fix/card-401-handle (2026-05-24) — 401 시 다른 화면과 동일하게 handleUnauthorized + redirect.
        // 기존: r.json() 직진 → toastFavoriteError 만 표시 (만료 사유 안내 누락).
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d) {
        // Wave AW.3-React Wave 4 fix (2026-05-16) — 토글 응답 키 정합.
        // POST /buyer-app/auction/:id/favorite 응답: { success, data: { favorited, favoriteCount } }
        if (d && d.success) {
          var favorited = !!(d.data && d.data.favorited);
          setIsFav(favorited);
          showToast(t(favorited ? 'toastFavoriteAdded' : 'toastFavoriteRemoved'), { variant: 'success' });
          if (onFavoriteChange) onFavoriteChange(auction.id, favorited);
        } else {
          setIsFav(prev);
          showToast(t('toastFavoriteError'), { variant: 'error' });
        }
      })
      .catch(function(err) {
        // unauthorized 분기는 이미 handleUnauthorized 가 navigate — UI 추가 동작 없음.
        if (err && err.message === 'unauthorized') return;
        setIsFav(prev);
        showToast(t('toastFavoriteError'), { variant: 'error' });
      })
      .finally(function() { setFavLoading(false); });
  };

  var bidCount = liveStats.bidCount || 0;
  var favCount = liveStats.favoriteCount || 0;
  // chore/dead-mask-fallback (2026-05-24) — mask 응답에 top-level highestBid 없음 → liveStats 직접.
  var highestBid = liveStats.highestBid || 0;
  // mask 응답: auction.myBid = { amount, status, ... } (myStatus 아님).
  var myBidAmount = auction.myBid && auction.myBid.amount;
  var remaining = formatRemaining(auction.endsAt, t);

  return (
    <div onClick={function() { if (onClick) onClick(auction); }}
      className="buyer-card-lift"
      style={{
        background: 'white', borderRadius: 12,
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
      {/* 썸네일 */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#f1f5f9' }}>
        {photoPath ? (
          <AuthImage path={photoPath} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
            {t('cardNoPhoto')}
          </div>
        )}
        {/* Wave UX-2.3 #16 — 캐러셀 prev/next 화살표 (사진 2장 이상) */}
        {photoCount > 1 && (
          <>
            <button type="button"
              onClick={function(e){ e.stopPropagation(); setPhotoIdx((photoIdx - 1 + photoCount) % photoCount); }}
              style={carouselBtn('left')}
              aria-label={t('photoPrev')}>‹</button>
            <button type="button"
              onClick={function(e){ e.stopPropagation(); setPhotoIdx((photoIdx + 1) % photoCount); }}
              style={carouselBtn('right')}
              aria-label={t('photoNext')}>›</button>
            {/* dots */}
            <div style={{
              position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
              display: 'flex', gap: 4, padding: '4px 8px',
              background: 'rgba(15,23,42,0.4)', borderRadius: 999,
            }}>
              {Array.from({ length: photoCount }).map(function(_, i) {
                return <span key={i} style={{
                  width: i === photoIdx ? 14 : 6, height: 5,
                  borderRadius: 999,
                  background: i === photoIdx ? 'white' : 'rgba(255,255,255,0.5)',
                  transition: 'width 0.2s',
                }} />;
              })}
            </div>
          </>
        )}
        {/* 즐겨찾기 토글 */}
        <button type="button" onClick={toggleFav}
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 36, height: 36,
            background: 'rgba(255,255,255,0.92)',
            border: 'none', borderRadius: '50%',
            cursor: favLoading ? 'wait' : 'pointer',
            fontSize: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          }}>
          {isFav ? '⭐' : '☆'}
        </button>
        {/* 남은 시간 */}
        {remaining && (
          <div style={{
            position: 'absolute', bottom: 10, left: 10,
            background: 'rgba(15,23,42,0.75)', color: 'white',
            padding: '4px 10px', borderRadius: 6,
            fontSize: 12, fontWeight: 700,
          }}>
            {remaining}
          </div>
        )}
        {/* 내 입찰 뱃지 */}
        {myBidAmount && (
          <div style={{
            position: 'absolute', top: 10, left: 10,
            background: '#3b82f6', color: 'white',
            padding: '4px 10px', borderRadius: 6,
            fontSize: 11, fontWeight: 700,
          }}>
            {t('cardMyBid', { amount: formatKrw(myBidAmount, t) })}
          </div>
        )}
      </div>

      {/* 정보 */}
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {car.year ? car.year + ' ' : ''}{car.model || auction.title || t('cardAuctionTitleFallback')}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 8 }}>
          {car.number && <span>{car.number}</span>}
          {formatMileage(car.mileage) && <span>·</span>}
          {formatMileage(car.mileage) && <span>{formatMileage(car.mileage)}</span>}
        </div>
        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('cardHighestBid')}</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#1B3A5C' }}>
            {highestBid > 0 ? formatKrw(highestBid, t) : <span style={{ color: '#94a3b8', fontSize: 13 }}>{t('cardNoBid')}</span>}
          </div>
        </div>
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8' }}>
          <span>{t('cardBidsLabel', { count: bidCount })}</span>
          <span>{t('cardFavLabel', { count: favCount })}</span>
        </div>
      </div>
    </div>
  );
}

// Wave UX-2.3 #16 — 캐러셀 prev/next 버튼 스타일 (좌/우 동일, position 분기)
function carouselBtn(side) {
  return {
    position: 'absolute',
    [side]: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 28, height: 28,
    border: 'none', borderRadius: '50%',
    background: 'rgba(15,23,42,0.55)', color: 'white',
    fontSize: 18, fontWeight: 700,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0, lineHeight: 1,
  };
}

// perf/card-react-memo (2026-05-24) — List 페이지 (20+ 카드) 부모 setState 시 재렌더 차단.
// 정책: inline onClick / onFavoriteChange 핸들러 ref 변경은 무시 (closure 가 캡처한 auction.id 가 동일하면 동작 같음).
// 비교 대상: auction 의 시각적 필드만 — id, endsAt, photos.length, myFavorited, liveStats, myBid.amount.
function areCardPropsEqual(prev, next) {
  var a = prev.auction || {};
  var b = next.auction || {};
  if (a.id !== b.id) return false;
  if (a.endsAt !== b.endsAt) return false;
  if (a.myFavorited !== b.myFavorited) return false;
  var aPhotos = (a.photos && a.photos.length) || 0;
  var bPhotos = (b.photos && b.photos.length) || 0;
  if (aPhotos !== bPhotos) return false;
  var aStats = a.liveStats || {};
  var bStats = b.liveStats || {};
  if ((aStats.highestBid || 0) !== (bStats.highestBid || 0)) return false;
  if ((aStats.bidCount || 0) !== (bStats.bidCount || 0)) return false;
  if ((aStats.favoriteCount || 0) !== (bStats.favoriteCount || 0)) return false;
  var aMyBid = (a.myBid && a.myBid.amount) || 0;
  var bMyBid = (b.myBid && b.myBid.amount) || 0;
  if (aMyBid !== bMyBid) return false;
  var aTitle = (a.car && a.car.model) || a.title || '';
  var bTitle = (b.car && b.car.model) || b.title || '';
  if (aTitle !== bTitle) return false;
  // onClick / onFavoriteChange 핸들러 ref 변경 무시 — 같은 카드 의미 동일
  return true;
}

export default React.memo(AuctionBuyerCard, areCardPropsEqual);
